import {
  Activity,
  Heart,
  Zap,
  TrendingUp,
  Moon,
  Brain,
  RefreshCw,
  Droplets,
  Wind,
  Thermometer,
} from "lucide-react";
import welcomeBg from "../../assets/homebg.png";
import { Card } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { Button } from "../../components/ui/button";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { message } from "antd";
import BottomNavBar from "../../components/BottomNavBar";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import { Skeleton } from "../../components/ui/skeleton";
import GluceraDateCalendar from "../../components/common/GluceraDateCalendar";
import { getLocalDateString } from "../../utils/homePageUtils";
import { homeMetricIconPrimary, homeSyncButton } from "../../utils/homeBlueTheme";
import { cn } from "../../lib/utils";


import fitnessService, { FitnessData } from "../../services/FitnessService";
import fitbitAuthService from "../../services/FitbitAuthService";

const FitnessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();





  const [searchParams] = useSearchParams();
  const [isProcessingFitbit, setIsProcessingFitbit] = useState(false);
  const hasExchangedCode = useRef(false);

  // Handle Fitbit OAuth Callback if parameters are present in URL
  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");

    if (code && state && !isProcessingFitbit && !hasExchangedCode.current) {
      const handleFitbitCallback = async () => {
        hasExchangedCode.current = true;
        setIsProcessingFitbit(true);
        const hide = message.loading("Connecting your Fitbit account...", 0);

        try {
          await fitbitAuthService.exchangeCode(code, state);
          message.success("Fitbit connected successfully!");

          // Force a full page refresh back to the clean URL (without code/state)
          // This ensures all services and components reset correctly.
          hide();
          window.location.href = window.location.pathname;
        } catch (error) {
          console.error("[FitnessPage] Fitbit exchange failed:", error);
          message.error("Failed to connect Fitbit. Please try again.");
          hide();
          setIsProcessingFitbit(false);
        }
      };

      handleFitbitCallback();
    }
  }, [searchParams]);


  // Initialize with passed date from navigation state or today
  const initialDate = location.state?.selectedDateObj
    ? location.state.selectedDateObj
    : location.state?.selectedDate
      ? (() => {
        const [year, month, day] = location.state.selectedDate
          .split("-")
          .map(Number);
        return new Date(year, month - 1, day);
      })()
      : new Date();

  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);
  const [currentMonth, setCurrentMonth] = useState<Date>(initialDate);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [fitnessData, setFitnessData] = useState<FitnessData | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [fitbitNotConnected, setFitbitNotConnected] = useState(false);
  const [stressData, setStressData] = useState<{
    score: number;
    level: string;
  } | null>(null);
  // range string for weekly data (used under several cards)
  const [weeklyRange, setWeeklyRange] = useState<string>("");

  // Get days in current month
  // helper to format a start/end string similar to home page
  const formatTrendRange = (start?: string, end?: string) => {
    if (!start || !end) return "";
    const sd = new Date(start).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const ed = new Date(end).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${sd} - ${ed}`;
  };

  // Helper to format numeric values for display (rounding/fraction control)
  const formatNumber = (
    value: number | string | undefined,
    maximumFractionDigits = 0
  ) => {
    const n = Number(value || 0);
    return n.toLocaleString(undefined, { maximumFractionDigits });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const handleFitnessDateSelect = (dateString: string) => {
    const [year, month, day] = dateString.split("-").map(Number);
    setSelectedDate(new Date(year, month - 1, day));
    setIsCalendarOpen(false);
  };

  const fetchFitnessData = async (date: string) => {
    try {
      setLoading(true);
      const result = (await fitnessService.getDailyFitnessData(date)) as any;

      // Check if response contains Fitbit token missing error (401)
      const is401 =
        (result?.status === "401" || result?.status === 401) &&
        (result?.error === "Fitbit token not found" ||
          (result?.message || "").toLowerCase().includes("connect fitbit"));

      if (is401) {
        setFitbitNotConnected(true);
        setFitnessData(null);
        setLoading(false);
        return;
      }

      // If it's not a 401, we want to show the data screen (dashboard)
      // even if there was an error or the data is empty.
      setFitbitNotConnected(false);

      // Handle successful data or fall back to zeros
      if (result && result.data) {
        setFitnessData(result.data);
        if (result.data.stress_score !== undefined && result.data.stress_level) {
          // Round stress score properly
          const roundedScore = Math.round(result.data.stress_score * 10) / 10;
          
          setStressData({
            score: roundedScore,
            level: result.data.stress_level,
          });
        }
      } else {
        // Successful API call but no data, or unsuccessful (non-401)
        const emptyFitnessData: FitnessData = {
          steps: 0,
          steps_goal: 10000,
          calories_burned: 0,
          calories_goal: 2500,
          active_zone_minutes: 0,
          active_zone_minutes_goal: 30,
          resting_heart_rate: 0,
          sleep_minutes: 0,
          sleep_goal: 8,
          weight_kg: 0,
          distance_km: 0,
          floors: 0,
          stress_score: 0,
          stress_level: "Neutral",
          weekly_steps: { this_week: [] },
        };
        setFitnessData(emptyFitnessData);
        setStressData(null);
      }
    } catch (error: any) {
      console.error("[FitnessPage] Error in fetchFitnessData:", error);
      // Check if error is specifically Fitbit token not found (401)
      const errorResponse = error?.response?.data || {};
      const errorStatus = error?.response?.status || error?.status;
      const errorMessage = errorResponse.error || errorResponse.message || error?.message || "";

      if (
        (errorStatus === 401 || errorStatus === "401") &&
        (errorMessage.toLowerCase().includes("token not found") ||
          errorMessage.toLowerCase().includes("connect fitbit"))
      ) {
        setFitbitNotConnected(true);
        setFitnessData(null);
      } else {
        // For other errors (e.g. 500 or network), show empty dashboard
        const emptyData: FitnessData = {
          steps: 0,
          steps_goal: 10000,
          calories_burned: 0,
          calories_goal: 2500,
          active_zone_minutes: 0,
          active_zone_minutes_goal: 30,
          resting_heart_rate: 0,
          sleep_minutes: 0,
          sleep_goal: 8,
          weight_kg: 0,
          distance_km: 0,
          floors: 0,
          stress_score: 0,
          stress_level: "Neutral",
          weekly_steps: { this_week: [] },
        };
        setFitbitNotConnected(false);
        setFitnessData(emptyData);
        setStressData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFitbitLogin = async () => {
    await fitbitAuthService.loginRedirect((err: string) => {
      message.error(err);
    });
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`;

      await fitnessService.syncFitbitData(dateStr);
      await fetchFitnessData(dateStr);
    } catch (error: any) {
      // Still fetch data even if sync had issues
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`;
      await fetchFitnessData(dateStr);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    // 🔧 Fix: Skip fetching data if we're in the middle of a Fitbit callback
    // The handleFitbitCallback effect will handle the refresh/load.
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    if (code && state) {
      return;
    }

    // Auto-refresh token if expired on page load
    const initializeFitbit = async () => {
      if (fitnessService.hasFitbitConnected()) {
        try {
          await fitbitAuthService.ensureValidToken();
        } catch (error) {
          // Token refresh failed silently
        }
      }
    };

    initializeFitbit();

    // 🔧 Fix: Use local timezone instead of UTC to prevent date shift
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    fetchFitnessData(dateStr);
  }, [selectedDate, searchParams]);

  const getActivityMetrics = () => {
    if (!fitnessData) {
      return [];
    }

    // Get goals from nested goals object or direct properties
    const stepsGoal =
      fitnessData.goals?.steps || fitnessData.steps_goal || 10000;
    const caloriesGoal =
      fitnessData.goals?.calories || fitnessData.calories_goal || 2500;
    const activeMinutesGoal =
      fitnessData.goals?.active_zone_minutes ||
      fitnessData.active_zone_minutes_goal ||
      30;

    const stepsProgress = Math.round(
      ((fitnessData.steps || 0) / stepsGoal) * 100
    );
    const caloriesProgress = Math.round(
      ((fitnessData.calories_burned || 0) / caloriesGoal) * 100
    );
    const activeMinutesProgress = Math.round(
      ((fitnessData.active_zone_minutes || 0) / activeMinutesGoal) * 100
    );

      return [
      {
        icon: Activity,
        label: "Steps",
        value: formatNumber(fitnessData.steps),
        unit: `of ${formatNumber(stepsGoal)}`,
        progress: stepsProgress,
        color: "bg-blue-500",
        lightColor: "bg-blue-50",
      },
      {
        icon: Heart,
        label: "Heart Rate",
        value: fitnessData.resting_heart_rate || "--",
        unit: "BPM",
        progress: null,
        color: "bg-red-500",
        lightColor: "bg-red-50",
      },
      {
        icon: Zap,
        label: "Calories Burned",
        value: formatNumber(fitnessData.calories_burned),
        unit: `of ${formatNumber(caloriesGoal)} cal`,
        progress: caloriesProgress,
        color: "bg-orange-500",
        lightColor: "bg-orange-50",
      },
      {
        icon: TrendingUp,
        label: "Active Minutes",
        value: fitnessData.active_zone_minutes || 0,
        unit: `of ${activeMinutesGoal} min`,
        progress: activeMinutesProgress,
        color: "bg-green-500",
        lightColor: "bg-green-50",
      },
    ];
  };

  const activityMetrics = getActivityMetrics();

  const getWeeklyActivity = () => {
    if (!fitnessData?.weekly_steps?.this_week) {
      return [];
    }

    return fitnessData.weekly_steps.this_week.map(dayData => ({
      day: dayData.day,
      steps: dayData.steps,
      progress: Math.round(
        ((dayData.steps || 0) / (fitnessData.steps_goal || 6000)) * 100
      ),
    }));
  };

  const weeklyActivity = getWeeklyActivity();



  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image - Only within container */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast - Only within container */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {loading ? (
          <div className="space-y-4 pt-2">
            {/* Header skeleton */}
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <Skeleton className="h-4 w-24 rounded-md" />
                <Skeleton className="h-7 w-40 rounded-lg" />
                <Skeleton className="h-3.5 w-32 rounded-md" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-10 w-20 rounded-xl" />
                <Skeleton className="h-10 w-10 rounded-xl" />
              </div>
            </div>
            {/* Activity cards skeleton */}
            <div className="grid grid-cols-2 gap-3">
              <Skeleton className="h-36 rounded-xl" />
              <Skeleton className="h-36 rounded-xl" />
              <Skeleton className="h-36 rounded-xl" />
              <Skeleton className="h-36 rounded-xl" />
            </div>
            {/* Sleep & Stress skeleton */}
            <Skeleton className="h-24 rounded-2xl" />
            <Skeleton className="h-24 rounded-2xl" />
            {/* Body metrics skeleton */}
            <div className="grid grid-cols-2 gap-2">
              <Skeleton className="h-24 rounded-xl" />
              <Skeleton className="h-24 rounded-xl" />
            </div>
            {/* HRV skeleton */}
            <Skeleton className="h-28 rounded-2xl" />
            {/* Weekly summary skeleton */}
            <Skeleton className="h-48 rounded-2xl" />
          </div>
        ) : fitbitNotConnected ? (
          <div className="flex flex-col items-center justify-center px-4 py-12">
            <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-orange-100">
              <Activity className="h-10 w-10 text-orange-500" />
            </div>
            <h2 className="mb-2 text-center text-2xl font-semibold text-foreground">
              Connect Fitbit to View Data
            </h2>
            <p className="mb-6 max-w-md text-center text-subhead text-muted-foreground">
              You haven't connected your Fitbit yet. Connect now to
              automatically track your activities, steps, calories, sleep, and
              get personalized health insights.
            </p>
            <Button
              onClick={handleFitbitLogin}
              className="mb-4 bg-orange-500 hover:bg-orange-600"
            >
              <Activity className="mr-2 h-4 w-4" />
              Connect Now
            </Button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between pt-2">
              <div>
                <p className="text-compact-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  {isToday(selectedDate) ? "Today's Activity" : "Daily Activity"}
                </p>
                <h1 className="mt-0.5 text-2xl font-bold tracking-tight text-foreground">
                  {selectedDate.toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "short",
                    day: "numeric",
                  })}
                </h1>
              </div>
              <div className="flex items-center space-x-2">
                {/* Sync Fitbit Button - Show for all past and current dates */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSync}
                  disabled={syncing || loading}
                  className={cn("h-10 rounded-xl border px-3 disabled:opacity-50", homeSyncButton)}
                  title="Sync Fitbit data for selected date"
                >
                  <RefreshCw
                    className={cn("h-4 w-4", homeMetricIconPrimary, syncing && "animate-spin")}
                  />
                  <span className={cn("ml-2 text-subhead font-medium", homeMetricIconPrimary)}>
                    {syncing ? "Syncing..." : "Sync"}
                  </span>
                </Button>

                <GluceraDateCalendar
                  open={isCalendarOpen}
                  onOpenChange={setIsCalendarOpen}
                  currentMonth={currentMonth}
                  onCurrentMonthChange={setCurrentMonth}
                  selectedDate={getLocalDateString(selectedDate)}
                  onDateSelect={handleFitnessDateSelect}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {activityMetrics.map((metric, i) => {
                const Icon = metric.icon;
                const accents = [
                  "from-blue-500/[0.07] via-blue-500/[0.02] dark:from-blue-500/[0.1]",
                  "from-red-500/[0.07] via-red-500/[0.02] dark:from-red-500/[0.1]",
                  "from-orange-500/[0.07] via-orange-500/[0.02] dark:from-orange-500/[0.1]",
                  "from-green-500/[0.07] via-green-500/[0.02] dark:from-green-500/[0.1]",
                ];
                const iconBgs = [
                  "border-blue-200/70 bg-blue-50/90 dark:border-blue-800/50 dark:bg-blue-950/40",
                  "border-red-200/70 bg-red-50/90 dark:border-red-800/50 dark:bg-red-950/40",
                  "border-orange-200/70 bg-orange-50/90 dark:border-orange-800/50 dark:bg-orange-950/40",
                  "border-green-200/70 bg-green-50/90 dark:border-green-800/50 dark:bg-green-950/40",
                ];
                const iconColors = [
                  "text-blue-600 dark:text-blue-400",
                  "text-red-600 dark:text-red-400",
                  "text-orange-600 dark:text-orange-400",
                  "text-green-600 dark:text-green-400",
                ];
                const routes = [
                  `/glucera${GluceraNavigationRoutes.STEPS_DETAIL}`,
                  `/glucera${GluceraNavigationRoutes.HEART_RATE_DETAIL}`,
                  `/glucera${GluceraNavigationRoutes.CALORIES_DETAIL}`,
                  `/glucera${GluceraNavigationRoutes.ACTIVE_MINUTES_DETAIL}`,
                ];
                return (
                  <Card
                    key={i}
                    className="relative cursor-pointer overflow-hidden rounded-xl border border-border/65 bg-card shadow-sm transition-shadow duration-200 hover:shadow-md dark:border-border/50"
                    onClick={() => {
                      const year = selectedDate.getFullYear();
                      const month = String(
                        selectedDate.getMonth() + 1
                      ).padStart(2, "0");
                      const day = String(selectedDate.getDate()).padStart(
                        2,
                        "0"
                      );
                      const dateStr = `${year}-${month}-${day}`;
                      navigate(routes[i], {
                        state: {
                          selectedDate: dateStr,
                          selectedDateObj: selectedDate,
                        },
                      });
                    }}
                  >
                    <div className={`pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b ${accents[i]} to-transparent`} />
                    <div className="relative p-3 sm:p-3.5">
                      <div className="mb-3 flex items-start justify-between gap-2">
                        <p className="text-compact-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                          {metric.label}
                        </p>
                        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${iconBgs[i]}`}>
                          <Icon className={`h-4 w-4 ${iconColors[i]}`} strokeWidth={2.25} />
                        </div>
                      </div>
                      <div className="flex items-end gap-1 tabular-nums">
                        <span className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                          {metric.value}
                        </span>
                      </div>
                      <p className="mt-0.5 text-compact-sm font-medium tabular-nums text-muted-foreground">
                        {metric.unit}
                      </p>
                      {metric.progress !== null && (
                        <Progress
                          value={Math.min(metric.progress as number, 100)}
                          className="mt-3 h-1.5 bg-muted"
                        />
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Sleep Card */}
            <Card className="relative overflow-hidden rounded-xl border border-border/65 bg-card shadow-sm transition-shadow duration-200 hover:shadow-md dark:border-border/50">
              <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-blue-500/[0.07] via-blue-500/[0.02] to-transparent dark:from-blue-500/[0.1]" />
              <div className="relative flex items-center justify-between p-3 sm:p-3.5">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-blue-200/70 bg-blue-50/90 dark:border-blue-800/50 dark:bg-blue-950/40">
                    <Moon className="h-4 w-4 text-blue-600 dark:text-blue-400" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-compact-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                      Sleep
                    </p>
                    {weeklyRange && (
                      <p className="text-compact-xs font-medium text-muted-foreground">
                        {weeklyRange}
                      </p>
                    )}
                    <div className="mt-0.5 flex items-baseline gap-1 tabular-nums">
                      <span className="text-xl font-bold tracking-tight text-foreground">
                        {fitnessData
                          ? fitnessData.sleep_hours
                            ? fitnessData.sleep_hours
                            : ((fitnessData.sleep_minutes || 0) / 60).toFixed(1) + " hrs"
                          : "--"}
                      </span>
                      {fitnessData && !fitnessData.sleep_hours && (
                        <span className="text-xs font-medium text-muted-foreground">hrs</span>
                      )}
                    </div>
                    <p className="text-compact-xs font-medium tabular-nums text-muted-foreground">
                      of {fitnessData?.goals?.sleep || fitnessData?.sleep_goal || 8} hrs goal
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 rounded-lg text-compact-sm font-semibold uppercase tracking-[0.08em] text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/40"
                  onClick={() => {
                    const year = selectedDate.getFullYear();
                    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
                    const day = String(selectedDate.getDate()).padStart(2, "0");
                    const dateStr = `${year}-${month}-${day}`;
                    navigate(`/glucera${GluceraNavigationRoutes.SLEEP_DETAIL}`, {
                      state: { selectedDate: dateStr, selectedDateObj: selectedDate },
                    });
                  }}
                >
                  View Details
                </Button>
              </div>
            </Card>

            {/* Stress Score Card */}
            <Card
              className="relative cursor-pointer overflow-hidden rounded-xl border border-border/65 bg-card shadow-sm transition-shadow duration-200 hover:shadow-md dark:border-border/50"
              onClick={() => {
                const year = selectedDate.getFullYear();
                const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
                const day = String(selectedDate.getDate()).padStart(2, "0");
                const dateStr = `${year}-${month}-${day}`;
                navigate(`/glucera${GluceraNavigationRoutes.STRESS_SCORE_DETAIL}`, {
                  state: { selectedDate: dateStr, selectedDateObj: selectedDate },
                });
              }}
            >
              <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-orange-500/[0.07] via-orange-500/[0.02] to-transparent dark:from-orange-500/[0.1]" />
              <div className="relative flex items-center justify-between p-3 sm:p-3.5">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-orange-200/70 bg-orange-50/90 dark:border-orange-800/50 dark:bg-orange-950/40">
                    <Brain className="h-4 w-4 text-orange-600 dark:text-orange-400" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-compact-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                      Stress Score
                    </p>
                    {weeklyRange && (
                      <p className="text-compact-xs font-medium text-muted-foreground">
                        {weeklyRange}
                      </p>
                    )}
                    <div className="mt-0.5 flex items-baseline gap-1 tabular-nums">
                      <span className="text-xl font-bold tracking-tight text-foreground">
                        {stressData?.score
                          ? (stressData.score % 1 === 0
                              ? Math.round(stressData.score)
                              : stressData.score.toFixed(1))
                          : "--"}
                      </span>
                      <span className="text-xs font-medium text-muted-foreground">/ 100</span>
                    </div>
                    <p className="text-compact-xs font-medium text-muted-foreground">
                      {stressData?.level || "No data"}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
            {/* Body Metrics */}
            <div className="grid grid-cols-2 gap-3">
              <Card className="relative overflow-hidden rounded-xl border border-border/65 bg-card shadow-sm dark:border-border/50">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-violet-500/[0.07] via-violet-500/[0.02] to-transparent dark:from-violet-500/[0.1]" />
                <div className="relative p-3 text-center sm:p-3.5">
                  <p className="text-compact-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                    Weight
                  </p>
                  <div className="mt-1 flex items-baseline justify-center gap-1 tabular-nums">
                    <span className="text-2xl font-bold tracking-tight text-foreground">
                      {fitnessData?.preferred_weight_unit === "lbs"
                        ? fitnessData?.weight_lbs
                          ? fitnessData.weight_lbs.toFixed(1)
                          : "--"
                        : fitnessData?.weight_kg
                          ? fitnessData.weight_kg.toFixed(1)
                          : "--"}
                    </span>
                    <span className="text-xs font-medium text-muted-foreground">
                      {fitnessData?.preferred_weight_unit === "lbs" ? "lbs" : "kg"}
                    </span>
                  </div>
                  {(() => {
                    const isLbs = fitnessData?.preferred_weight_unit === "lbs";
                    const targetVal = isLbs
                      ? fitnessData?.target_weight_lbs
                      : fitnessData?.target_weight_kg;
                    if (targetVal && targetVal > 0) {
                      return (
                        <p className="mt-1 text-compact-xs font-medium tabular-nums text-blue-600 dark:text-blue-400">
                          Target: {targetVal.toFixed(1)}{isLbs ? "lbs" : "kg"}
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
              </Card>
              <Card className="relative overflow-hidden rounded-xl border border-border/65 bg-card shadow-sm dark:border-border/50">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-teal-500/[0.07] via-teal-500/[0.02] to-transparent dark:from-teal-500/[0.1]" />
                <div className="relative p-3 text-center sm:p-3.5">
                  <p className="text-compact-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                    Distance
                  </p>
                  <div className="mt-1 flex items-baseline justify-center gap-1 tabular-nums">
                    <span className="text-2xl font-bold tracking-tight text-foreground">
                      {fitnessData?.preferred_distance_unit === "mi"
                        ? fitnessData?.distance_miles != null
                          ? formatNumber(fitnessData.distance_miles, 2)
                          : "--"
                        : fitnessData?.distance_km != null
                          ? formatNumber(fitnessData.distance_km, 1)
                          : "--"}
                    </span>
                    <span className="text-xs font-medium text-muted-foreground">
                      {fitnessData?.preferred_distance_unit === "mi" ? "mi" : "km"}
                    </span>
                  </div>
                </div>
              </Card>
            </div>
            {/* HRV Card */}
            <Card
              className="relative cursor-pointer overflow-hidden rounded-xl border border-border/65 bg-card shadow-sm transition-shadow duration-200 hover:shadow-md dark:border-border/50"
              onClick={() => {
                const year = selectedDate.getFullYear();
                const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
                const day = String(selectedDate.getDate()).padStart(2, "0");
                const dateStr = `${year}-${month}-${day}`;
                navigate(`/glucera${GluceraNavigationRoutes.HRV_DETAIL}`, {
                  state: { selectedDate: dateStr, selectedDateObj: selectedDate },
                });
              }}
            >
              <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-pink-500/[0.07] via-pink-500/[0.02] to-transparent dark:from-pink-500/[0.1]" />
              <div className="relative flex items-start justify-between p-3 sm:p-3.5">
                <div className="flex-1 min-w-0">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-compact-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                        Heart Rate Variability
                      </p>
                      <p className="text-compact-xs font-medium text-muted-foreground">
                        Last night's reading
                      </p>
                    </div>
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-pink-200/70 bg-pink-50/90 dark:border-pink-800/50 dark:bg-pink-950/40">
                      <Heart className="h-4 w-4 text-pink-600 dark:text-pink-400" strokeWidth={2.25} />
                    </div>
                  </div>
                  <div className="flex items-baseline gap-1 tabular-nums">
                    <span className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                      {fitnessData?.hrv_rmssd && fitnessData.hrv_rmssd > 0
                        ? Math.round(fitnessData.hrv_rmssd)
                        : "--"}
                    </span>
                    <span className="text-xs font-medium text-muted-foreground">ms</span>
                  </div>
                </div>
                <div className="ml-3 flex h-12 items-end gap-1">
                  {(() => {
                    const baseHrv = fitnessData?.hrv_rmssd || 40;
                    const variation = [0.8, 0.9, 1.1, 0.85, 1.05, 1.15, 1.2];
                    return variation.map((factor, idx) => {
                      const height = Math.min(95, Math.max(50, ((baseHrv * factor) / baseHrv) * 75));
                      return (
                        <div
                          key={idx}
                          className="w-1.5 rounded-full bg-pink-400 dark:bg-pink-500"
                          style={{ height: `${height}%` }}
                        />
                      );
                    });
                  })()}
                </div>
              </div>
            </Card>

            {/* Weekly Summary */}
            <Card className="relative overflow-hidden rounded-xl border border-border/65 bg-card shadow-sm dark:border-border/50">
              <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-blue-500/[0.07] via-blue-500/[0.02] to-transparent dark:from-blue-500/[0.1]" />
              <div className="relative p-3 sm:p-3.5">
                <div className="mb-3">
                  <p className="text-compact-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    Activity & trends
                  </p>
                  <h3 className="mt-0.5 text-base font-bold tracking-tight text-foreground sm:text-lg">
                    This Week
                  </h3>
                </div>

                <div className="space-y-2.5">
                  {weeklyActivity.map((day, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="w-8 text-compact-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        {day.day}
                      </span>
                      <div className="flex-1">
                        <div className="mb-1 flex items-center justify-between">
                          <span className="text-xs font-bold tabular-nums text-foreground">
                            {formatNumber(day?.steps)}
                          </span>
                          <span className="text-compact-xs font-medium text-muted-foreground">
                            steps
                          </span>
                        </div>
                        <Progress value={Math.min(day.progress, 100)} className="h-1.5 bg-muted" />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-3 border-t border-border/50 pt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-compact-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                      Weekly Average
                    </span>
                    <span className="text-sm font-bold tabular-nums tracking-tight text-foreground">
                      {formatNumber(fitnessData?.weekly_steps?.weekly_average)} steps
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Health Vitals Header */}
            <div>
              <p className="text-compact-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Vitals
              </p>
              <h3 className="mt-0.5 text-base font-bold tracking-tight text-foreground sm:text-lg">
                Health Vitals
              </h3>
            </div>

            {/* Health Vitals Cards - Side by Side */}
            <div className="grid grid-cols-3 gap-3">
              {/* SpO2 Card */}
              <Card
                className="relative cursor-pointer overflow-hidden rounded-xl border border-border/65 bg-card shadow-sm transition-shadow duration-200 hover:shadow-md dark:border-border/50"
                onClick={() => {
                  navigate(`/glucera${GluceraNavigationRoutes.SPO2_DETAIL}`, {
                    state: {
                      selectedDate: selectedDate.toISOString().split("T")[0],
                      selectedDateObj: selectedDate,
                      fitnessData: fitnessData,
                    },
                  });
                }}
              >
                <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-purple-500/[0.07] via-purple-500/[0.02] to-transparent dark:from-purple-500/[0.1]" />
                <div className="relative p-3 text-center">
                  <div className="mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-lg border border-purple-200/70 bg-purple-50/90 dark:border-purple-800/50 dark:bg-purple-950/40">
                    <Droplets className="h-4 w-4 text-purple-600 dark:text-purple-400" strokeWidth={2.25} />
                  </div>
                  <div className="text-base font-bold tabular-nums tracking-tight text-foreground">
                    {fitnessData?.spo2_percent ? `${fitnessData.spo2_percent}%` : "--"}
                  </div>
                  <div className="mt-1 text-compact-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    SpO2
                  </div>
                </div>
              </Card>

              {/* Breathing Rate Card */}
              <Card
                className="relative cursor-pointer overflow-hidden rounded-xl border border-border/65 bg-card shadow-sm transition-shadow duration-200 hover:shadow-md dark:border-border/50"
                onClick={() => {
                  navigate(`/glucera${GluceraNavigationRoutes.BREATHING_DETAIL}`, {
                    state: {
                      selectedDate: selectedDate.toISOString().split("T")[0],
                      selectedDateObj: selectedDate,
                      fitnessData: fitnessData,
                    },
                  });
                }}
              >
                <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-green-500/[0.07] via-green-500/[0.02] to-transparent dark:from-green-500/[0.1]" />
                <div className="relative p-3 text-center">
                  <div className="mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-lg border border-green-200/70 bg-green-50/90 dark:border-green-800/50 dark:bg-green-950/40">
                    <Wind className="h-4 w-4 text-green-600 dark:text-green-400" strokeWidth={2.25} />
                  </div>
                  <div className="text-base font-bold tabular-nums tracking-tight text-foreground">
                    {fitnessData?.breathing_rate || "--"}
                  </div>
                  <div className="mt-1 text-compact-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    Breathing
                  </div>
                </div>
              </Card>

              {/* Skin Temperature Card */}
              <Card
                className="relative cursor-pointer overflow-hidden rounded-xl border border-border/65 bg-card shadow-sm transition-shadow duration-200 hover:shadow-md dark:border-border/50"
                onClick={() => {
                  navigate(`/glucera${GluceraNavigationRoutes.TEMPERATURE_DETAIL}`, {
                    state: {
                      selectedDate: selectedDate.toISOString().split("T")[0],
                      selectedDateObj: selectedDate,
                      fitnessData: fitnessData,
                    },
                  });
                }}
              >
                <div className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-orange-500/[0.07] via-orange-500/[0.02] to-transparent dark:from-orange-500/[0.1]" />
                <div className="relative p-3 text-center">
                  <div className="mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-lg border border-orange-200/70 bg-orange-50/90 dark:border-orange-800/50 dark:bg-orange-950/40">
                    <Thermometer className="h-4 w-4 text-orange-600 dark:text-orange-400" strokeWidth={2.25} />
                  </div>
                  <div className="text-base font-bold tabular-nums tracking-tight text-foreground">
                    {fitnessData?.preferred_temperature_unit === "f"
                      ? fitnessData?.skin_temp_fahrenheit
                        ? `${fitnessData.skin_temp_fahrenheit.toFixed(1)}°F`
                        : "--"
                      : fitnessData?.skin_temp_celsius
                        ? `${fitnessData.skin_temp_celsius.toFixed(1)}°C`
                        : "--"}
                  </div>
                  <div className="mt-1 text-compact-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    Skin Temp
                  </div>
                </div>
              </Card>
            </div>
          </>
        )}
      </div>

      <BottomNavBar activeIndex={2} />
    </div>
  );
};

export default FitnessPage;
